import React, { createContext, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { registerPushToken, unregisterPushToken } from "../lib/pushNotifications";
import { subscribe } from "../lib/refreshBus";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem("samrudhi_member_token"));

  // 2026-09-15 local-first perf fix: this used to be a plain useState+useEffect chain that
  // blocked the ENTIRE app behind a fresh network round-trip on every cold start. It's now a
  // react-query `useQuery` so a WebView relaunch can paint the last-known member (persisted
  // to localStorage, see lib/queryPersist.js) INSTANTLY, then silently revalidate against the
  // server in the background (staleTime:0 - always revalidates, never trusted for actions).
  const { data: member, isLoading } = useQuery({
    queryKey: ["member-me"],
    queryFn: async () => {
      try {
        const { data } = await api.get("/member/me");
        registerPushToken();
        return data;
      } catch (err) {
        // Only a real 401 (invalid/expired/invalidated session) should ever log the member
        // out. A network error/timeout/5xx must NEVER wipe a still-valid token - rethrowing
        // (instead of swallowing) lets react-query keep serving the last good cached member
        // untouched, and the visibility/online listeners below will retry automatically.
        if (err?.response?.status === 401) {
          localStorage.removeItem("samrudhi_member_token");
          setHasToken(false);
          return null;
        }
        throw err;
      }
    },
    enabled: hasToken,
    staleTime: 0,
    retry: false,
  });

  const loading = hasToken && isLoading;

  useEffect(() => {
    const retry = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("samrudhi_member_token")) {
        queryClient.refetchQueries({ queryKey: ["member-me"] });
      }
    };
    document.addEventListener("visibilitychange", retry);
    window.addEventListener("online", retry);

    // 2026-09-14 live refresh: any successful member-side mutation (purchase, repurchase,
    // withdrawal, scratch card claim, KYC update, etc.) re-fetches this member's own
    // profile snapshot (wallet balance, activation status, KYC status, rank...) so every
    // page that reads it via useAuth() updates itself without a manual reload. Debounced
    // so a burst of several calls from one action only triggers one refetch.
    let debounceHandle = null;
    const unsubscribe = subscribe("member", () => {
      if (!localStorage.getItem("samrudhi_member_token")) return;
      clearTimeout(debounceHandle);
      debounceHandle = setTimeout(() => queryClient.invalidateQueries({ queryKey: ["member-me"] }), 300);
    });

    return () => {
      document.removeEventListener("visibilitychange", retry);
      window.removeEventListener("online", retry);
      clearTimeout(debounceHandle);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (token) => {
    localStorage.setItem("samrudhi_member_token", token);
    setHasToken(true);
    return queryClient.refetchQueries({ queryKey: ["member-me"] });
  };

  const logout = () => {
    // Fire the unregister call BEFORE clearing the token (it needs the still-valid auth
    // header) so this device's push token is unlinked from this member - prevents the next
    // member who logs in on the same device from also receiving this member's notifications.
    unregisterPushToken().finally(() => {
      localStorage.removeItem("samrudhi_member_token");
      setHasToken(false);
      queryClient.setQueryData(["member-me"], null);
    });
  };

  const refresh = () => queryClient.refetchQueries({ queryKey: ["member-me"] });

  return (
    <AuthContext.Provider value={{ member, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
